<template>
  <div class="stock-so-movements stock-linked-section">
    <v-subheader class="stock-linked-section__title px-0 font-weight-bold">
      {{ $t("STOCK.SALES_ORDERS.RELATED_MOVEMENTS") }}
    </v-subheader>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
    <v-data-table
      v-if="salesOrderId"
      :headers="headers"
      :items="items"
      :loading="loading"
      hide-default-footer
      :items-per-page="-1"
      class="stock-so-movements__table stock-linked-table"
    >
      <template v-slot:item.code="{ item }">
        <router-link :to="movementViewRoute(item)">
          {{ item.code }}
        </router-link>
        <div v-if="item.parent_action" class="caption grey--text text--darken-1">
          {{ $t("STOCK.RECEIPTS.PARENT_DOCUMENT") }}:
          <router-link :to="movementViewRoute(item.parent_action)">
            {{ item.parent_action.code }}
          </router-link>
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip small :color="receiptStatusColor(item.status)" dark>
          {{ receiptStatusLabel(item.status) }}
        </v-chip>
      </template>
      <template v-slot:item.counterparty="{ item }">
        {{ counterpartyLabel(item) }}
      </template>
      <template v-slot:item.warehouse="{ item }">
        {{ warehouseLabel(item) }}
      </template>
      <template v-slot:item.action_date="{ item }">
        {{ item.action_date || "-" }}
      </template>
      <template v-slot:item.items_count="{ item }">
        {{ item.items_count != null ? item.items_count : "-" }}
      </template>
      <template v-slot:item.total_gross="{ item }">
        {{ formatMovementMoney(item.total_gross, item.currency_code) }}
      </template>
      <template v-slot:item.total_cost_of_goods_net="{ item }">
        {{ formatFinancialValue(item.total_cost_of_goods_net, item.currency_code) }}
      </template>
      <template v-slot:item.total_margin_net="{ item }">
        <span :class="marginTextClass(item.total_margin_net)">
          {{ formatFinancialValue(item.total_margin_net, item.currency_code) }}
        </span>
      </template>
      <template v-slot:item.margin_pct="{ item }">
        <span :class="marginTextClass(item.total_margin_net)">
          {{ movementMarginPct(item) }}
        </span>
      </template>
      <template v-slot:item.transport_order_code="{ item }">
        <router-link
          v-if="item.transport_order_id"
          :to="{ name: 'stock-transport-orders-view', params: { id: item.transport_order_id } }"
        >
          {{ item.transport_order_code }}
        </router-link>
        <span v-else>-</span>
      </template>
    </v-data-table>
    <v-alert v-if="!loading && !items.length" type="info" dense text class="mt-2">
      {{ $t("STOCK.SALES_ORDERS.NO_RELATED_MOVEMENTS") }}
    </v-alert>
  </div>
</template>

<script>
import stockMovementRowDisplayMixin from "@/views/partials/stock/stockMovementRowDisplay.mixin";
import {
  buildLinkedDocumentHeaders,
  linkedSaleFinancialHeaders,
  linkedTransportOrderReferenceHeader,
} from "@/views/partials/stock/stockLinkedTableHeaders";
import { GET_STOCK_SO_MOVEMENTS } from "@/store/stock/stockSalesOrders.module";

export default {
  name: "StockSalesOrderMovementsTable",
  mixins: [stockMovementRowDisplayMixin],
  props: {
    salesOrderId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      items: [],
    };
  },
  computed: {
    headers() {
      return [
        ...buildLinkedDocumentHeaders(this.$t.bind(this), {
          counterpartyLabelKey: "STOCK.SALES_ORDERS.CUSTOMER",
          warehouseLabelKey: "STOCK.SALES.SOURCE_WAREHOUSE",
          dateLabelKey: "STOCK.SALES.DATE",
        }),
        ...linkedSaleFinancialHeaders(this.$t.bind(this)),
        linkedTransportOrderReferenceHeader(this.$t.bind(this)),
      ];
    },
  },
  watch: {
    salesOrderId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.reload();
        } else {
          this.items = [];
        }
      },
    },
  },
  methods: {
    marginTextClass(value) {
      if (value == null || Number(value) === 0) {
        return "";
      }
      return Number(value) > 0 ? "success--text" : "error--text";
    },
    movementViewRoute(row) {
      const category = row.action_type && row.action_type.category;
      if (category === "sale") {
        return { name: "stock-sales-view", params: { id: row.id } };
      }
      if (category === "write_off") {
        return { name: "stock-write-offs-view", params: { id: row.id } };
      }
      if (category === "transfer") {
        return { name: "stock-transfers-view", params: { id: row.id } };
      }
      return { name: "stock-receipts-view", params: { id: row.id } };
    },
    reload() {
      if (!this.salesOrderId) {
        return Promise.resolve();
      }

      this.loading = true;
      return this.$store
        .dispatch(GET_STOCK_SO_MOVEMENTS, {
          salesOrderId: this.salesOrderId,
          itemsPerPage: 100,
        })
        .then((response) => {
          this.items = response.data || [];
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/views/partials/stock/stockLinkedTable.scss";

::v-deep .stock-so-movements__table th {
  white-space: nowrap;
}

.stock-so-movements__table a {
  text-decoration: none;
  font-weight: 500;
}
.stock-so-movements__table a:hover {
  text-decoration: underline;
}
</style>
